import { makeAutoObservable } from "mobx";

import { BN } from "@utils/BN";
import { formatAmount } from "@utils/Format";

import type { SubAccount } from "@src/services";
import { vaultService } from "@src/services";

import type { Token } from "./BalanceStore";
import { DIALOG_TYPE } from "./DialogStore";
import type { RootStore } from "./RootStore";

export class VaultStore {
  constructor(private readonly rootStore: RootStore) {
    makeAutoObservable(this);
  }

  deposit = async (token: Token, amount: BN, depositTo: "funding" | "trading" = "trading", tradingIndex?: number) => {
    if (this.guardGeoRestricted()) {
      return;
    }

    const { accountStore, balanceStore, toastStore, dialogStore } = this.rootStore;
    const { wallet, fundingSubAccount } = accountStore;

    if (!wallet?.account || !fundingSubAccount) return;

    const isLinked = await accountStore.checkAccountsLinkedToBlockchain(true);
    if (!isLinked) {
      toastStore.show({
        type: "error",
        title: "Accounts Not Linked",
        content: "Please link your accounts to blockchain before depositing",
      });
      return;
    }

    let tradingSubAccount;
    if (depositTo === "trading") {
      tradingSubAccount =
        tradingIndex !== undefined ? accountStore.tradingSubAccounts[tradingIndex] : accountStore.tradingSubAccount;

      if (!tradingSubAccount) {
        return;
      }
    }

    await vaultService.deposit({
      wallet,
      fundingAddress: fundingSubAccount.address,
      tradingAddress: tradingSubAccount?.address,
      tokenAddress: token.address,
      amount,
      depositTo,
    });

    balanceStore.fetchOnChainBalance(wallet.account.address.toString());

    toastStore.show({
      type: "success",
      title: "Deposit Successful",
      content: `Deposited ${formatAmount(amount, token.decimals)} ${token.symbol} to the vault. Balance may take a moment to update.`,
    });

    dialogStore.close(DIALOG_TYPE.DEPOSIT);
  };

  withdraw = async (token: Token, amount: BN) => {
    if (this.guardGeoRestricted()) {
      return;
    }

    const { accountStore, toastStore, balanceStore, dialogStore } = this.rootStore;
    const { wallet, fundingSubAccount } = accountStore;

    if (!wallet?.account || !fundingSubAccount) return;

    await vaultService.withdraw({
      wallet,
      fundingAddress: fundingSubAccount.address,
      tokenAddress: token.address,
      amount,
    });

    balanceStore.fetchOnChainBalance(wallet.account.address.toString());

    toastStore.show({
      type: "success",
      title: "Withdrawal Successful",
      content: `Withdrew ${formatAmount(amount, token.decimals)} ${token.symbol} from the funding account. Balance may take a moment to update.`,
    });

    dialogStore.close(DIALOG_TYPE.WITHDRAW);
  };

  transfer = async (
    token: Token,
    amount: BN,
    from: "funding" | "trading",
    to: "funding" | "trading",
    fromIndex?: number,
    toIndex?: number,
  ) => {
    if (this.guardGeoRestricted()) {
      return;
    }

    const { accountStore, toastStore, balanceStore, dialogStore } = this.rootStore;
    const { wallet, fundingSubAccount } = accountStore;

    if (!wallet?.account || !fundingSubAccount) return;

    const { vaultFrom, vaultFromType } = this.resolveVaultSource(from, fromIndex);
    const { vaultTo, vaultToType } = this.resolveVaultDestination(to, toIndex);

    if (!vaultFrom || !vaultTo) return;

    if (from === "funding" && to === "trading") {
      await vaultService.transferRequest({
        wallet,
        fromSubAddress: vaultFrom,
        toSubAddress: vaultTo,
        vaultToType,
        amount,
      });

      toastStore.show({
        type: "success",
        title: "Transfer Request Created",
        content: `Transfer request for ${formatAmount(amount, token.decimals)} ${token.symbol} created. Balance may take a moment to update.`,
      });
    } else {
      await vaultService.transfer({
        wallet,
        vaultFrom,
        vaultTo,
        vaultFromType,
        vaultToType,
        amount,
      });

      toastStore.show({
        type: "success",
        title: "Transfer Successful",
        content: `Transferred ${formatAmount(amount, token.decimals)} ${token.symbol}. Balance may take a moment to update.`,
      });
    }

    balanceStore.fetchOnChainBalance(wallet.account.address.toString());
    dialogStore.close(DIALOG_TYPE.TRANSFER);
  };

  withdrawFromTradingToFunding = async (token: Token, amount: BN, fromTradingIndex?: number) => {
    if (this.guardGeoRestricted()) {
      return;
    }

    const { accountStore, toastStore, balanceStore, dialogStore } = this.rootStore;
    const { fundingSubAccount } = accountStore;

    if (!fundingSubAccount) {
      return;
    }

    await accountStore.updateNoncesFromBlockchain();

    let tradingSubAccount =
      fromTradingIndex !== undefined
        ? accountStore.tradingSubAccounts[fromTradingIndex]
        : accountStore.activeTradingAccount;

    if (!tradingSubAccount) {
      return;
    }

    tradingSubAccount = await this.ensureTradingAccountHasPrivateKey(tradingSubAccount);
    if (!tradingSubAccount) return;

    try {
      // Convert BN to human-readable string (e.g., "1.5" instead of "1500000")
      const amountString = BN.formatUnits(amount, token.decimals).toString();

      await vaultService.withdrawFromTrading({
        tradingAccount: tradingSubAccount,
        fundingAddress: fundingSubAccount.address,
        tokenAddress: token.address,
        amount: amountString,
      });

      balanceStore.fetchOnChainBalance(fundingSubAccount.address);

      toastStore.show({
        type: "success",
        title: "Transfer Successful",
        content: `Transferred ${formatAmount(amount, token.decimals)} ${token.symbol} from trading to funding. Balance may take a moment to update.`,
      });

      dialogStore.close(DIALOG_TYPE.TRANSFER);
    } catch (error) {
      toastStore.show({
        type: "error",
        title: "Transfer Failed",
        content: `Failed to transfer: ${error instanceof Error ? error.message : String(error)}`,
      });
    }
  };

  private resolveVaultSource = (from: "funding" | "trading", fromIndex?: number) => {
    const { accountStore } = this.rootStore;
    const { fundingSubAccount } = accountStore;

    if (from === "funding") {
      return { vaultFrom: fundingSubAccount?.address, vaultFromType: "Funding" };
    }

    const tradingAccount =
      fromIndex !== undefined ? accountStore.tradingSubAccounts[fromIndex] : accountStore.tradingSubAccount;

    if (!tradingAccount) {
      return { vaultFrom: undefined, vaultFromType: "Cross" };
    }

    return { vaultFrom: tradingAccount.address, vaultFromType: "Cross" };
  };

  private resolveVaultDestination = (to: "funding" | "trading", toIndex?: number) => {
    const { accountStore } = this.rootStore;
    const { fundingSubAccount } = accountStore;

    if (to === "funding") {
      return { vaultTo: fundingSubAccount?.address, vaultToType: "Funding" };
    }

    const tradingAccount =
      toIndex !== undefined ? accountStore.tradingSubAccounts[toIndex] : accountStore.tradingSubAccount;

    if (!tradingAccount) {
      return { vaultTo: undefined, vaultToType: "Cross" };
    }

    return { vaultTo: tradingAccount.address, vaultToType: "Cross" };
  };

  reset = () => {};

  private ensureTradingAccountHasPrivateKey = async (tradingSubAccount: SubAccount) => {
    const { accountStore, toastStore } = this.rootStore;

    if (!tradingSubAccount.privateKey && tradingSubAccount.orderIndex !== undefined) {
      const restoredAccount = await accountStore.generateTradingAccountByNonce(tradingSubAccount.orderIndex.toString());

      if (restoredAccount?.privateKey) {
        restoredAccount.nonce = tradingSubAccount.nonce;
        tradingSubAccount = restoredAccount;
      } else {
        toastStore.show({
          type: "error",
          title: "Missing Private Key",
          content: "Unable to generate private key for trading account. Please try reconnecting your wallet.",
        });
        return null;
      }
    }

    if (!tradingSubAccount.privateKey) {
      toastStore.show({
        type: "error",
        title: "Missing Private Key",
        content: "Trading account private key is required for this operation",
      });
      return null;
    }

    return tradingSubAccount;
  };

  private guardGeoRestricted = () => {
    if (!this.rootStore.geoFenceStore.isRestricted) {
      return false;
    }

    this.rootStore.toastStore.show({
      type: "warning",
      title: "Limited Functionality",
      content: "Unavailable in restricted jurisdiction.",
    });
    return true;
  };
}
